<!-- Plan seats -->
@if (showSeats) {
  <section class="pf-seats mb-5" aria-labelledby="pf-seats-title">
    <div class="mb-3 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
      <h2 id="pf-seats-title" class="text-base font-semibold text-gray-800 dark:text-white/90">Cupos de tu plan</h2>
      <p class="text-theme-sm text-gray-500 tabular-nums dark:text-gray-400">
        <span class="font-semibold text-gray-800 dark:text-white/90">{{ activeProfessionalsCount }}</span>
        de {{ paidProfessionalsCount }} en uso
      </p>
    </div>

    <div class="pf-seats__bar" [class.pf-seats__bar--dense]="seats.length > 12" role="img"
      [attr.aria-label]="activeProfessionalsCount + ' de ' + paidProfessionalsCount + ' cupos en uso'">
      @for (seat of seats; track $index) {
        <span class="pf-seat" [class.pf-seat--taken]="seat.taken"
          [title]="seat.taken ? (seat.name || 'Cupo en uso') : 'Cupo libre'"></span>
      }
    </div>

    <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
      <p class="text-theme-sm text-gray-500 dark:text-gray-400">
        @if (canCreateMore) {
          Puedes agregar {{ freeSeats }} profesional{{ freeSeats !== 1 ? 'es' : '' }} más sin costo adicional.
        } @else {
          Usaste todos tus cupos. Al agregar otro profesional te pediremos pagar un cupo adicional.
        }
      </p>
      @if (!canCreateMore) {
        <button type="button" class="pf-btn pf-btn--ghost" (click)="onCreateClient()">Comprar un cupo</button>
      }
    </div>
  </section>
}

<!-- Toolbar -->
<div class="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
  <div class="pf-segmented" role="radiogroup" aria-label="Filtrar por estado">
    @for (f of statusFilters; track f.value) {
      <button type="button" role="radio" class="pf-segmented__item"
        [attr.aria-checked]="statusFilter === f.value" (click)="onStatusFilterChange(f.value)">
        {{ f.label }}
        <span class="pf-segmented__count">{{ statusCount(f.value) }}</span>
      </button>
    }
  </div>

  <div class="flex items-center gap-2">
    <div class="relative flex-1 lg:flex-none">
      <svg class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" fill="none"
        stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="search" [value]="searchTerm" (input)="onSearch($any($event.target).value)"
        placeholder="Buscar por nombre o correo"
        aria-label="Buscar profesional"
        class="pf-input h-10 w-full pl-9 pr-3 lg:w-64" />
    </div>
    <button type="button" (click)="onCreateClient()"
      class="pf-btn pf-btn--primary inline-flex shrink-0 items-center gap-2"
      aria-label="Agregar profesional">
      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      <span class="hidden sm:inline">Agregar profesional</span>
    </button>
  </div>
</div>

<!-- Skeleton loader -->
@if (loading && filteredProfessionals.length === 0) {
  <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" role="status" aria-label="Cargando profesionales">
    @for (item of skeletonRows; track $index) {
      <div class="pf-card animate-pulse">
        <div class="flex items-center gap-4 p-5">
          <div class="size-14 rounded-full bg-gray-200 dark:bg-gray-800"></div>
          <div class="flex-1">
            <div class="mb-2 h-4 w-32 rounded bg-gray-200 dark:bg-gray-800"></div>
            <div class="h-3 w-24 rounded bg-gray-100 dark:bg-gray-800/70"></div>
          </div>
        </div>
        <div class="space-y-2 px-5 pb-5">
          <div class="h-3 w-44 rounded bg-gray-100 dark:bg-gray-800/70"></div>
          <div class="h-3 w-28 rounded bg-gray-100 dark:bg-gray-800/70"></div>
        </div>
      </div>
    }
  </div>
}

<!-- Empty state -->
@if (!loading && filteredProfessionals.length === 0) {
  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-white/[0.03]">
    @if (searchTerm) {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Nadie coincide con "{{ searchTerm }}"</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Busca por nombre, apellido o correo.
      </p>
      <button type="button" (click)="onSearch(''); searchTerm = ''" class="pf-btn pf-btn--ghost">
        Limpiar búsqueda
      </button>
    } @else if (statusFilter !== 'all') {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">
        No hay profesionales {{ statusFilter === 'active' ? 'activos' : 'inactivos' }} en esta página
      </h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Cambia el filtro para ver al resto del equipo.
      </p>
      <button type="button" (click)="onStatusFilterChange('all')" class="pf-btn pf-btn--ghost">
        Ver a todos
      </button>
    } @else {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Aún no tienes profesionales</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Agrega a las personas de tu equipo para asignarles servicios y recibir citas en su agenda.
      </p>
      <button type="button" (click)="onCreateClient()" class="pf-btn pf-btn--primary inline-flex items-center gap-2">
        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Agregar tu primer profesional
      </button>
    }
  </div>
}

<!-- Team -->
@if (filteredProfessionals.length > 0) {
  <ul class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
    [class.opacity-60]="loading" [class.pointer-events-none]="loading">
    @for (professional of filteredProfessionals; track professional.userId) {
      @let name = fullName(professional);
      @let services = professionalServicesMap()[professional.userId] ?? [];
      <li class="pf-card" [class.pf-card--inactive]="!professional.isActive">
        <!-- Identity -->
        <div class="flex items-start gap-4 p-5 pb-4">
          <div class="pf-avatar" [style.--pf-color]="professional.color || 'var(--color-brand-500)'"
            [title]="'Color en la agenda'">
            @if (professional.avatarUrl) {
              <img [src]="professional.avatarUrl" alt="" class="h-full w-full object-cover" loading="lazy" />
            } @else {
              <span>{{ (professional.firstName?.[0] || '?') | uppercase }}{{ (professional.lastName?.[0] || '') | uppercase }}</span>
            }
          </div>
          <div class="min-w-0 flex-1 pt-1">
            <h3 class="pf-name truncate">{{ name || 'Sin nombre' }}</h3>
            <p class="pf-meta truncate">
              @if (!professional.isActive) {
                Inactivo, no recibe citas
              } @else if (professional.createdAt) {
                En el equipo desde {{ formatDate(professional.createdAt) }}
              } @else {
                Activo
              }
            </p>
          </div>
        </div>

        <!-- User (license) -->
        <div class="pf-access px-5 mb-3" [class.pf-access--on]="professional.hasAccount"
          [class.pf-access--pending]="!professional.hasAccount && professional.hasPendingInvitation">
          <svg class="size-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
          </svg>
          <span class="flex-1 truncate">
            @if (professional.hasAccount) {
              Usuario activo
            } @else if (professional.hasPendingInvitation) {
              Invitación enviada
            } @else {
              Sin usuario para entrar
            }
          </span>
          @if (!professional.hasAccount && canInviteUsers) {
            <a class="pf-access__action" [routerLink]="['/settings/users']" [queryParams]="{ invite: professional.userId }">
              {{ professional.hasPendingInvitation ? 'Reenviar' : 'Dar acceso' }}
            </a>
          }
        </div>

        <!-- Contact -->
        @if (professional.email || professional.phone) {
          <dl class="pf-contact px-5">
            @if (professional.email) {
              <div>
                <dt class="sr-only">Correo</dt>
                <svg class="size-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                <dd class="truncate">{{ professional.email }}</dd>
              </div>
            }
            @if (professional.phone) {
              <div>
                <dt class="sr-only">Teléfono</dt>
                <svg class="size-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                </svg>
                <dd class="truncate tabular-nums">{{ professional.phone }}</dd>
              </div>
            }
          </dl>
        }

        <!-- Services -->
        <div class="mt-4 mb-4 px-5">
          @if (services.length) {
            <ul class="flex flex-wrap gap-1.5" [attr.aria-label]="'Servicios de ' + name">
              @for (svc of services.slice(0, 3); track svc.serviceId) {
                <li class="pf-chip" [title]="svc.name">{{ svc.name }}</li>
              }
              @if (services.length > 3) {
                <li class="pf-chip pf-chip--more" [title]="serviceNames(services.slice(3))">
                  +{{ services.length - 3 }}
                </li>
              }
            </ul>
          } @else {
            <p class="pf-meta">Sin servicios asignados todavía</p>
          }
        </div>

        <!-- Actions -->
        <div class="pf-card__footer">
          <button type="button" role="switch" class="pf-toggle" [attr.aria-checked]="professional.isActive"
            [attr.aria-label]="(professional.isActive ? 'Desactivar a ' : 'Activar a ') + name"
            (click)="openToggleStatus(professional)">
            <span class="pf-switch"><span class="pf-switch__thumb"></span></span>
            <span class="pf-switch__label">{{ professional.isActive ? 'Activo' : 'Inactivo' }}</span>
          </button>
          <div class="flex items-center gap-1">
            <button type="button" class="pf-icon-btn" (click)="onEditClient(professional.userId)"
              [attr.aria-label]="'Editar a ' + name" title="Editar">
              <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
            </button>
            <button type="button" class="pf-icon-btn pf-icon-btn--danger" (click)="openDelete(professional)"
              [attr.aria-label]="'Eliminar a ' + name" title="Eliminar">
              <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          </div>
        </div>
      </li>
    }
  </ul>

  <!-- Pagination -->
  @if (totalPages > 1) {
    <nav class="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row" aria-label="Paginación">
      <p class="text-theme-sm text-gray-500 tabular-nums dark:text-gray-400">
        {{ (page - 1) * pageSize + 1 }}–{{ page * pageSize > totalCount ? totalCount : page * pageSize }} de {{ totalCount }}
      </p>
      <div class="flex items-center gap-1">
        <button type="button" (click)="onPageChange(page - 1)" [disabled]="page <= 1" class="pf-page" aria-label="Página anterior">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
        </button>
        @for (p of paginationArray; track $index) {
          @if ($index + 1 === page || $index + 1 === 1 || $index + 1 === totalPages || ($index + 1 >= page - 1 && $index + 1 <= page + 1)) {
            <button type="button" (click)="onPageChange($index + 1)" class="pf-page"
              [attr.aria-current]="($index + 1) === page ? 'page' : null">
              {{ $index + 1 }}
            </button>
          } @else if ($index + 1 === page - 2 || $index + 1 === page + 2) {
            <span class="inline-flex size-9 items-center justify-center text-sm text-gray-400" aria-hidden="true">…</span>
          }
        }
        <button type="button" (click)="onPageChange(page + 1)" [disabled]="page >= totalPages" class="pf-page" aria-label="Página siguiente">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </nav>
  }
}

<!-- Create/Edit Modal -->
<app-modal [open]="open" [title]="modalTitle" [size]="'md'" [bodyComponent]="PeopleAddForm" [closeOnBackdrop]="false"
  [bodyInputs]="modalInputs" (confirm)="onModalConfirm($event)" (close)="open = false" [isSaving]="isSaving"
  confirmButtonText="Guardar">
</app-modal>

<!-- Delete Confirmation Modal -->
<app-confirm-modal [(open)]="confirmOpen"
  title="Eliminar profesional"
  [subtitle]="toDeleteName ? 'Estás a punto de eliminar a \'' + toDeleteName + '\'. Esta acción no se puede deshacer.' : 'Esta acción no se puede deshacer, ¿deseas continuar?'"
  cancelText="No, volver" confirmText="Sí, eliminar"
  variant="danger" [closeOnBackdrop]="true" (confirm)="onConfirmDelete()" (cancel)="toDeleteId = null; toDeleteName = null">
</app-confirm-modal>

<!-- Toggle Status Confirmation Modal -->
<app-confirm-modal [(open)]="confirmToggleOpen"
  [title]="toToggleProfessional?.isActive ? 'Desactivar profesional' : 'Activar profesional'"
  [subtitle]="toToggleProfessional ? (toToggleProfessional.isActive
    ? fullName(toToggleProfessional) + ' dejará de recibir citas y liberará su cupo del plan.'
    : fullName(toToggleProfessional) + ' volverá a recibir citas y ocupará un cupo del plan.') : ''"
  cancelText="Cancelar"
  [confirmText]="toToggleProfessional?.isActive ? 'Desactivar' : 'Activar'"
  [variant]="toToggleProfessional?.isActive ? 'warning' : 'primary'"
  [closeOnBackdrop]="true"
  (confirm)="onConfirmToggleStatus()"
  (cancel)="toToggleProfessional = null">
</app-confirm-modal>
